// src/pages/app/NotificationsPage.jsx — In-app activity & alerts center
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { notificationsApi } from "@/lib/api";
import { toast } from "sonner";
import {
  Bell,
  CheckCircle2,
  XCircle,
  Clock,
  Plane,
  Receipt,
  CheckCheck,
  Building2,
  Banknote,
  ArrowRight,
  Filter,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const TYPE_ICONS = {
  travel_request_pending: Clock,
  travel_request_approved: CheckCircle2,
  travel_request_rejected: XCircle,
  booking_required: Plane,
  itinerary_uploaded: Plane,
  expense_claim_pending: Clock,
  expense_claim_approved: CheckCircle2,
  expense_claim_rejected: XCircle,
  expense_for_settlement: Banknote,
  expense_settled: Banknote,
};

const TYPE_COLORS = {
  travel_request_approved: "text-emerald-600 bg-emerald-50 border-emerald-200",
  expense_claim_approved: "text-emerald-600 bg-emerald-50 border-emerald-200",
  expense_settled: "text-emerald-600 bg-emerald-50 border-emerald-200",
  travel_request_rejected: "text-red-600 bg-red-50 border-red-200",
  expense_claim_rejected: "text-red-600 bg-red-50 border-red-200",
  travel_request_pending: "text-amber-600 bg-amber-50 border-amber-200",
  expense_claim_pending: "text-amber-600 bg-amber-50 border-amber-200",
  booking_required: "text-blue-600 bg-blue-50 border-blue-200",
  itinerary_uploaded: "text-indigo-600 bg-indigo-50 border-indigo-200",
  expense_for_settlement: "text-purple-600 bg-purple-50 border-purple-200",
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // 'all' | 'unread'

  const loadNotifications = async () => {
    try {
      const { data } = await notificationsApi.list();
      setNotifications(data || []);
    } catch (err) {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleMarkRead = async (id, e) => {
    e?.stopPropagation();
    try {
      await notificationsApi.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
    } catch {
      toast.error("Failed to mark as read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Failed to mark all as read");
    }
  };

  const handleNavigate = async (n) => {
    if (!n.is_read) {
      await handleMarkRead(n.id);
    }
    if (n.reference_type === "travel_request" && n.reference_id) {
      navigate(`/app/travel-requests/${n.reference_id}`);
    } else if (n.reference_type === "expense_claim" && n.reference_id) {
      navigate(`/app/expense-claims/${n.reference_id}`);
    }
  };

  const filtered = notifications.filter((n) => {
    if (filter === "unread") return !n.is_read;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 flex items-center gap-2.5">
            <Bell className="w-6 h-6 text-blue-600" />
            Notifications & Alerts
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Live updates on your trip requests, manager approvals, itineraries, and expense claims
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors shadow-xs shrink-0 self-start sm:self-auto cursor-pointer"
          >
            <CheckCheck className="w-4 h-4 text-blue-600" />
            Mark All as Read ({unreadCount})
          </button>
        )}
      </div>

      {/* Main Content */}
      <Card className="border-slate-200/80 shadow-sm bg-white">
        <CardHeader className="border-b border-slate-100 py-3.5 px-6 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilter("all")}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
                filter === "all"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "text-slate-600 hover:bg-slate-100",
              )}
            >
              All Notifications ({notifications.length})
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
                filter === "unread"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "text-slate-600 hover:bg-slate-100",
              )}
            >
              Unread ({unreadCount})
            </button>
          </div>
        </CardHeader>

        <CardContent className="p-0 divide-y divide-slate-100">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : !filtered.length ? (
            <div className="text-center py-16 px-4">
              <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-semibold text-sm sm:text-base">No notifications found</p>
              <p className="text-slate-400 text-xs mt-1">
                {filter === "unread"
                  ? "You have caught up on all unread notifications!"
                  : "Actions on trips or expense claims will trigger live alerts here."}
              </p>
            </div>
          ) : (
            filtered.map((n) => {
              const IconComponent = TYPE_ICONS[n.type] || Bell;
              const colorCls =
                TYPE_COLORS[n.type] || "text-slate-600 bg-slate-100 border-slate-200";

              return (
                <div
                  key={n.id}
                  onClick={() => handleNavigate(n)}
                  className={cn(
                    "p-4 sm:px-6 flex items-start gap-4 transition-colors cursor-pointer group hover:bg-slate-50/80",
                    !n.is_read ? "bg-blue-50/40" : "bg-white",
                  )}
                >
                  <div
                    className={cn(
                      "w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 mt-0.5 shadow-xs",
                      colorCls,
                    )}
                  >
                    <IconComponent className="w-5 h-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3
                        className={cn(
                          "text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors",
                          !n.is_read && "font-bold",
                        )}
                      >
                        {n.title}
                      </h3>
                      <span className="text-[11px] text-slate-400 shrink-0 font-medium">
                        {n.created_at
                          ? new Date(n.created_at).toLocaleString("en-IN", {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : ""}
                      </span>
                    </div>

                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                      {n.message}
                    </p>

                    <div className="mt-2.5 flex items-center gap-3">
                      {n.reference_id && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 group-hover:underline">
                          View details <ArrowRight className="w-3 h-3" />
                        </span>
                      )}
                      {!n.is_read && (
                        <button
                          onClick={(e) => handleMarkRead(n.id, e)}
                          className="text-[11px] text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                        >
                          Mark as read
                        </button>
                      )}
                    </div>
                  </div>

                  {!n.is_read && (
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600 shrink-0 mt-2" />
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
